// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import _, { lowerCase } from 'lodash';
import PropTypes from 'prop-types';
import moment from 'moment';
import { IconButton, Card, TextField } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

// Internal Dependencies
import { connect } from 'react-redux';
import Photo from './Photo';
import MaterialDatePicker from '../../../../../components/Common/form/MaterialDatePicker';
import Icons from '../../../../../components/Common/Icons';
import Colours from '../../../../../styles/colours';
import ApiService from '../../../../../services/apiService';
import OverLoading from '../../../../../components/Loading';
import { Status, Error } from '../../../../../common/constants';
import MonthDayValidity from '../../../../../components/MonthDayValidity';
import SubscriptionLimitModal from '../../../../../components/SubscriptionLimitModal';
import { IsEmpty, IsNumber } from '../../../../../common/checks';
import { ConvertDateToSGTime, ConvertEndDate, ConvertStartDate } from '../../../../../common/util';
import MText from '../../../../../components/Common/MText';
import MButton from '../../../../../components/MButton';
import RadioBtnSelect from '../../../../../components/Common/RadioBtnSelect';
import CheckBox from '../../../../../components/Common/form/CheckBox';

const Gift = (props) => {
  const history = useHistory();
  const { pageId, giftId } = props.match.params;

  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [extendedExpiryDate, setExtendedExpiryDate] = useState(null);
  const [issuableQuantity, setIssuableQuantity] = useState('');
  const [termsAndCondition, setTermsAndCondition] = useState('');
  const [status, setStatus] = useState(Status.Draft);
  const [isHqAuthToIssue, setIsHqAuthToIssue] = useState(false);
  const [validityInMonths, setValidityInMonths] = useState(0);
  const [validityInDays, setValidityInDays] = useState(0);
  const [validitySetByStore, setValiditySetByStore] = useState(true);
  const [pauseIssuance, setPauseIssuance] = useState(false);
  const [type, setType] = useState(null);
  const [images, setImages] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isValidityByDateRange, setIsValidityByDateRange] = useState(true);
  const [planModal, setPlanModal] = useState(false);

  const headerFontSize = 14;
  const subHeaderFontSize = 12;

  const getGiftDataHandler = async () => {
    try {
      setLoading(true);
      const gift = await ApiService.getGift({ giftId, pageId });

      setDescription(gift.label);
      setStartDate(!IsEmpty(gift.startDate) ? ConvertDateToSGTime(new Date(gift.startDate)) : null);
      setEndDate(!IsEmpty(gift.endDate) ? ConvertDateToSGTime(new Date(gift.endDate)) : null);
      setExtendedExpiryDate(!IsEmpty(gift.extendedEndDate) ? ConvertDateToSGTime(new Date(gift.extendedEndDate)) : null);
      setIssuableQuantity(gift.issuableQty || '');
      setTermsAndCondition(gift.terms || '');
      setStatus(gift.status);
      setIsHqAuthToIssue(gift.isHqAuthToIssue || false);
      setPauseIssuance(gift.pauseIssuance || false);
      setType(gift.type);
      setImages(gift.images);
      setValidityInMonths(gift.validityInMonths);
      setValidityInDays(gift.validityInDays);
      setValiditySetByStore(gift.isValidityInDaysSetByStore);
      setData(gift);

      if (IsNumber(gift.validityInDays) || IsNumber(gift.validityInMonths)) {
        setIsValidityByDateRange(false);
      } else {
        setIsValidityByDateRange(true);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveGiftDataHandler = async () => {
    try {
      setLoading(true);
      let issuableQty = null;
      if (!IsEmpty(issuableQuantity)) {
        issuableQty = Number(issuableQuantity) <= 0 ? 0 : Number(issuableQuantity);
      }
      const payload = {
        id:                         giftId,
        label:                      description,
        startDate:                  (!IsEmpty(startDate) && isValidityByDateRange) ? ConvertStartDate(startDate) : null,
        endDate:                    (!IsEmpty(endDate) && isValidityByDateRange) ? ConvertEndDate(endDate) : null,
        extendedEndDate:            (!IsEmpty(extendedExpiryDate) && isValidityByDateRange) ? ConvertEndDate(extendedExpiryDate) : null,
        terms:                      termsAndCondition,
        type,
        issuableQty,
        validityInMonths:           isValidityByDateRange ? null : (validityInMonths || 0),
        validityInDays:             isValidityByDateRange ? null : (validityInDays || 0),
        isValidityInDaysSetByStore: isValidityByDateRange ? true : validitySetByStore, // Default value of isValidityInDaysSetByStore is true
        status,
        isHqAuthToIssue,
        pauseIssuance,
        images,
      };

      if (status === Status.Activated && IsEmpty(description)) {
        return toast.error(`Please provide a name for the ${lowerCase(type)}`);
      }

      if (isValidityByDateRange) {
        if ((!IsEmpty(startDate) && !IsEmpty(endDate)) && moment(endDate).isSameOrBefore(moment(startDate))) {
          return toast.error('End date should not exceed the start date!');
        } else if ((!IsEmpty(endDate) && !IsEmpty(extendedExpiryDate)) && moment(extendedExpiryDate).isSameOrBefore(moment(endDate))) {
          return toast.error('Extended Expiry date should not exceed the end date!');
        }
        if (status === Status.Activated && (IsEmpty(startDate) || IsEmpty(endDate))) {
          return toast.error(`Please provide the start and end dates the ${lowerCase(type)} is valid for`);
        }
      } else {
        if (!IsEmpty(validityInMonths) && validityInMonths < 0) {
          return toast.error(`Invalid value for 'months'`);
        }
        if (!IsEmpty(validityInMonths) && validityInDays < 0) {
          return toast.error(`Invalid value for 'days'`);
        }
        if (status === Status.Activated && (IsEmpty(validityInMonths) || validityInMonths === 0) && (IsEmpty(validityInDays) || validityInDays === 0)) {
          return toast.error('Validity in months and days cannot be both 0');
        }
      }

      if (payload.status === Status.Activated && !IsEmpty(payload.issuableQty) && payload.issuableQty <= 0) {
        return toast.error('Issuable quantity must be more than 0');
      }
      if (payload.status === Status.Activated && _.isEmpty(images)) {
        return toast.error(`To activate the ${lowerCase(type)}, please upload at least one image`);
      }

      await ApiService.updateGift({ giftId, pageId }, payload);
      toast.success(`${type} updated`);

      await getGiftDataHandler(); // get updated data
    } catch (err) {
      if (err.message === Error.MaxNumberLiveGifts) {
        setPlanModal(true);
      } else {
        toast.error(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const newDate = new Date(date);
    return moment(newDate).format('YYYY-MM-DD');
  };

  useEffect(() => {
    getGiftDataHandler();
  }, []);

  return (
    <div className="container-fluid">
      {loading && <OverLoading />}
      <SubscriptionLimitModal
        open={planModal}
        close={() => setPlanModal(false)}
        accountType={props.page.plan}
        page={props.page}
        type="gift"
      />

      <div className="row mt-3">
        <div className="col-12 d-flex flex-row align-items-center">
          <IconButton style={{ width: 50, height: 50 }} onClick={() => history.goBack()}>
            <Icons.ArrowBack colour={Colours.Black} />
          </IconButton>
          <MText text="Edit Voucher" size={18} bold />
        </div>
      </div>

      <div className="row px-4 pb-4">
        <Card className="col-12 px-4 pb-5">
          <div className="row mt-3">
            <div className="col-12">
              <MText text={`Voucher ID: ${giftId}`} size={14} colour={Colours.Gray1} />
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-12 col-lg-8 col-xl-6 d-flex flex-column">
              <MText size={headerFontSize} text={`Name of ${lowerCase(type)}`} />
              <MText size={subHeaderFontSize} colour={Colours.Gray} text="This name will be displayed to the customer" />
              <TextField
                className="mt-1"
                value={description || ''}
                onChange={(e) => setDescription(e.currentTarget.value)}
                variant="outlined"
                size="small"
                disabled={data?.status === Status.Deactivated}
                inputProps={{
                  style: {
                    fontSize: subHeaderFontSize,
                  },
                }}
              />
            </div>
          </div>

          <hr className="mt-4" />
          <div className="row mt-4">
            <div className="col-12 d-flex flex-column">
              <MText size={headerFontSize} text="Validity" />
              <MText size={subHeaderFontSize} colour={Colours.Gray} text={`Select either option for the ${lowerCase(type)} expiry:`} />
              <div className="row pl-2 mt-1">
                <div className="col-12 col-lg-6 col-xl-6 mb-2">
                  <RadioBtnSelect
                    options={[{
                      label:    'Start and end on specific date',
                      subLabel: '(Choose this if your voucher is only valid for a fixed period)',
                      value:    true,
                    }]}
                    value={isValidityByDateRange}
                    onChange={() => setIsValidityByDateRange(true)}
                    disabled={data?.status !== Status.Draft}
                  />
                  <div className="ml-4">
                    <MText size={subHeaderFontSize} text="Start Date" />
                    <MaterialDatePicker
                      minDate={false}
                      variant="outlined"
                      value={startDate}
                      disabled={data?.status !== Status.Draft || !isValidityByDateRange}
                      onChange={(e) => setStartDate(e ? formatDate(e.toString('yyyy-MM-dd')) : null)}
                      clearable
                      inputStyle={{ padding: '10.5px 14px', fontSize: subHeaderFontSize, width: '18rem' }}
                      className="mt-1 mb-0"
                    />

                    <MText className="mt-2" size={subHeaderFontSize} text="End Date" />
                    <MaterialDatePicker
                      minDate={!startDate ? false : new Date(startDate)}
                      variant="outlined"
                      value={endDate}
                      disabled={data?.status !== Status.Draft || !isValidityByDateRange}
                      onChange={(e) => setEndDate(e ? formatDate(e.toString('yyyy-MM-dd')) : null)}
                      clearable
                      inputStyle={{ padding: '10.5px 14px', fontSize: subHeaderFontSize, width: '18rem' }}
                      className="mt-1 mb-0"
                    />

                    {
                      (data?.status !== Status.Draft && isValidityByDateRange) && (
                        <>
                          <MText className="mt-3" size={subHeaderFontSize} text="Extended Expiry Date" />
                          <MaterialDatePicker
                            minDate={!endDate ? false : new Date(endDate)}
                            variant="outlined"
                            value={extendedExpiryDate}
                            disabled={data?.status !== Status.Activated || !isValidityByDateRange}
                            onChange={(value) => setExtendedExpiryDate(value ? formatDate(value.toString('yyyy-MM-dd')) : null)}
                            clearable
                            inputStyle={{ padding: '10.5px 14px', fontSize: subHeaderFontSize, width: '18rem' }}
                            style={{ borderRadius: '5px' }}
                            className="mt-1 mb-0"
                          />
                        </>
                      )
                    }
                  </div>
                </div>

                <div className="col-12 col-lg-6 col-xl-6 mt-2 mt-lg-0">
                  <RadioBtnSelect
                    options={[{
                      label:    'Expiry date calculated on issuance',
                      subLabel: `(Choose this if the voucher's expiry date is calculated on the day it is issued)`,
                      value:    true,
                    }]}
                    value={!isValidityByDateRange}
                    onChange={() => setIsValidityByDateRange(false)}
                    disabled={data?.status !== Status.Draft}
                  />
                  <div className="ml-4">
                    <MonthDayValidity
                      validityInMonths={validityInMonths}
                      validityInDays={validityInDays}
                      setValidityInMonths={setValidityInMonths}
                      setValidityInDays={setValidityInDays}
                      disabled={isValidityByDateRange}
                      textSize={subHeaderFontSize}
                      description={`E.g. if voucher is valid for 3 months on the day of issuance, then set 'month=3'`}
                    />

                    { /** Temporarily hidden while we setup the feature in CRM */}
                    {/* <MText className="mt-3" size={14} text="Store can override validity" />
                    <CheckBox
                      text="Allow individual store to override the validity of the coupon/voucher"
                      disabled={isValidityByDateRange}
                      onChange={(value) => setValiditySetByStore(value)}
                      checked={validitySetByStore}
                    /> */}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 mt-3">
              <MText text={`* Note: once ${lowerCase(type)} has been activated, the selection cannot be changed`} />
            </div>
          </div>

          <hr className="mt-4" />
          <div className="row mt-4">
            <div className="col-12 col-lg-8 col-xl-6 d-flex flex-column">
              <MText size={headerFontSize} text="Issuable Quantity" />
              <MText size={subHeaderFontSize} colour={Colours.Gray} text={`Limit the number of ${lowerCase(type)}s issuable`} />
              <TextField
                className="mt-1"
                value={issuableQuantity || ''}
                onChange={(e) => setIssuableQuantity(e.currentTarget.value)}
                variant="outlined"
                size="small"
                type="number"
                disabled={data?.status === Status.Deactivated}
                inputProps={{
                  min:   1,
                  style: {
                    fontSize: subHeaderFontSize,
                  },
                }}
              />
            </div>
            {
              (data?.status === Status.Activated) && (
                <div className="col-12 mt-2">
                  <MText size={headerFontSize} text="Pause Issuance" />
                  <MText size={subHeaderFontSize} text="Check this box to temporarily stop new voucher from being issued" />
                  <CheckBox
                    text={`Pause issuance of voucher ${pauseIssuance ? '(Uncheck to resume issuance)' : ''}`}
                    onChange={(value) => setPauseIssuance(value)}
                    checked={pauseIssuance}
                    textSize={subHeaderFontSize}
                    disabled={data?.status === Status.Deactivated}
                  />
                </div>
              )
            }
          </div>
          <hr className="mt-4" />

          <div className="row mt-4">
            <div className="col-12">
              <MText size={headerFontSize} text={`${type} photo`} />
              <Photo
                giftId={giftId}
                gift={data}
                onUpdateImages={setImages}
                disabled={data?.status === Status.Deactivated}
                textSize={subHeaderFontSize}
              />
            </div>
          </div>

          <hr className="mt-5" />
          <div className="row mt-4">
            <div className="col-12">
              <MText size={headerFontSize} text="Terms & Conditions" />
              <TextField
                className="mt-1 col-12 col-xl-8"
                multiline
                minRows={4}
                value={termsAndCondition || ''}
                onChange={(e) => setTermsAndCondition(e.currentTarget.value)}
                variant="outlined"
                size="small"
                disabled={data?.status === Status.Deactivated}
                inputProps={{
                  style: {
                    fontSize: subHeaderFontSize,
                  },
                }}
              />
            </div>

            {/** Temporarily hide this feature until we have developed feature for BDB staff to issue voucher */}
            {/* <div className="col-12">
              <MText className="mt-4" size={headerFontSize} text="HQ Issuance" />
              <CheckBox
                text="HQ business staff (admin and editor) can issue voucher"
                onChange={(value) => setIsHqAuthToIssue(value)}
                checked={isHqAuthToIssue}
                textSize={subHeaderFontSize}
                disabled={data?.status === Status.Deactivated}
              />
            </div> */}

            <div className="col-12">
              <MText className="mt-4" size={headerFontSize} text="Status" />
              <RadioBtnSelect
                className="ml-2"
                options={[
                  data?.status === Status.Draft ? { value: Status.Draft } : null,
                  data?.status !== Status.Deactivated ? { value: Status.Activated, subLabel: `(${type} is live and visible to public)` } : null,
                  data?.status !== Status.Draft
                    ? { value: Status.Deactivated, subLabel: `(Deactivates ${lowerCase(type)} and it will no longer be visible to public)` }
                    : null,
                ]}
                onChange={(value) => setStatus(value)}
                value={status}
                textSize={subHeaderFontSize}
              />
            </div>
          </div>

          {
            data?.status !== Status.Deactivated && (
              <div className="row mt-4">
                <div className="col-12 d-flex flex-column align-items-center">
                  <MButton
                    className="col-12 col-xl-8"
                    colour={Colours.Secondary}
                    textColour={Colours.White}
                    text="Save"
                    onClick={saveGiftDataHandler}
                  />
                </div>
              </div>
            )
          }
        </Card>
      </div>
    </div>
  );
};

Gift.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      pageId: PropTypes.string,
      giftId: PropTypes.string,
    }).isRequired,
  }).isRequired,
  page: PropTypes.shape({
    id:        PropTypes.number,
    name:      PropTypes.string,
    pageLogo:  PropTypes.string,
    pageType:  PropTypes.string,
    plan:      PropTypes.oneOf(['Ultimate', 'Premium', 'Free']),
    staffType: PropTypes.string,
  }),
};

Gift.defaultProps = {
  page: {},
};

const mapStateToProps = (state) => ({
  page: state.page.selected,
});

export default connect(mapStateToProps)(Gift);
